import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ChatAuthService } from '../services/chat-auth.service';

@Component({
  selector: 'app-chat-auth',
  templateUrl: './chat-auth.component.html',
  styleUrls: ['./chat-auth.component.scss']
})
export class ChatAuthComponent implements OnInit {

  accessCode: string = '';
  isLoading: boolean = false;
  isChecking: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  // List of valid access codes (4 characters)
  validAccessCodes: string[] = ['2026'];

  constructor(
    private authService: ChatAuthService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // If already authenticated, redirect to chat
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/milo']);
    }
  }

  /**
   * Handle token box input
   */
  onTokenBoxInput(event: any, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Only allow numeric input
    value = value.replace(/[^0-9]/g, '');
    input.value = value;

    // Update access code
    const codeArray = this.accessCode.split('');
    codeArray[index] = value;
    this.accessCode = codeArray.join('');

    // Clear error message on input
    this.errorMessage = '';

    // Move to next box if value entered
    if (value && index < 3) {
      const nextInput = document.querySelectorAll('.token-box')[index + 1] as HTMLInputElement;
      if (nextInput) {
        setTimeout(() => nextInput.focus(), 0);
      }
    }

    // Auto-validate when all 4 characters are entered
    if (this.accessCode.length === 4) {
      this.validateAccessCode();
    }
  }

  /**
   * Handle token box keydown (backspace, arrow keys)
   */
  onTokenKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    // Backspace - move to previous box
    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        const prevInput = document.querySelectorAll('.token-box')[index - 1] as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
          prevInput.value = '';

          // Update access code
          const codeArray = this.accessCode.split('');
          codeArray[index - 1] = '';
          this.accessCode = codeArray.join('');
        }
      }
    }

    // Left arrow - move to previous box
    if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.querySelectorAll('.token-box')[index - 1] as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Right arrow - move to next box
    if (event.key === 'ArrowRight' && index < 3) {
      const nextInput = document.querySelectorAll('.token-box')[index + 1] as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  /**
   * Handle paste event - auto-fill boxes
   */
  onTokenPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData ? clipboardData.getData('text') : '';
    const digits = pastedText.replace(/[^0-9]/g, '').substring(0, 4);

    this.accessCode = digits;

    // Update all inputs
    const inputs = document.querySelectorAll('.token-box') as NodeListOf<HTMLInputElement>;
    Array.from(inputs).forEach((input, index) => {
      input.value = digits[index] || '';
    });

    // Auto-validate if 4 digits pasted
    if (digits.length === 4) {
      setTimeout(() => this.validateAccessCode(), 100);
    }
  }

  /**
   * Validate access code
   */
  private validateAccessCode(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.accessCode.length !== 4) {
      return;
    }

    this.isChecking = true;

    setTimeout(() => {
      const isValid = this.validAccessCodes.includes(this.accessCode);

      if (isValid) {
        this.isChecking = false;
        this.authenticate();
      } else {
        this.isChecking = false;
        this.errorMessage = 'Invalid access code. Please try again.';
        this.accessCode = '';

        // Clear all inputs
        const inputs = document.querySelectorAll('.token-box') as NodeListOf<HTMLInputElement>;
        Array.from(inputs).forEach((input) => {
          input.value = '';
        });

        // Focus first input
        const firstInput = inputs[0];
        if (firstInput) {
          setTimeout(() => firstInput.focus(), 100);
        }
      }
    }, 300);
  }

  /**
   * Authenticate user
   */
  private authenticate(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Use the auth service to authenticate
    const isAuthenticated = this.authService.authenticate(this.accessCode);

    if (isAuthenticated) {
      this.successMessage = 'Welcome! Redirecting to chat... 🎉';
      this.accessCode = '';

      // Clear all inputs
      const inputs = document.querySelectorAll('.token-box') as NodeListOf<HTMLInputElement>;
      Array.from(inputs).forEach((input) => {
        input.value = '';
      });

      // Redirect after brief delay
      setTimeout(() => {
        this.router.navigate(['/milo']);
      }, 500);
    } else {
      this.errorMessage = 'Authentication failed. Please try again.';
      this.isLoading = false;
      this.accessCode = '';

      // Clear all inputs
      const inputs = document.querySelectorAll('.token-box') as NodeListOf<HTMLInputElement>;
      Array.from(inputs).forEach((input) => {
        input.value = '';
      });
    }
  }

  /**
   * Navigate back to dashboard
   */
  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}
